import Step from '../../constants/Step'
import { Cycle } from '../cycle/Cycle'
import {
    createOrderProduct,
    IOrderProductFactory,
    OrderProduct,
} from '../order-product/OrderProduct'

export const OrderStatus = {
    OPEN: 'O',
    ACCEPTED: 'P',
    READY_FOR_PICKUP: 'Y',
    PICKED_UP: 'F',
    DELIVERED: 'C',
    DECLINED: 'D',
    CANCELED: 'I',
    RETURN_REQUEST: 'A',
    RETURN_CONFIRMED: 'G',
    RETURN_DECLINED: 'E',
    RETURNED: 'H',
    BACKORDERED: 'B',
    // custom
    MOVED_TO_IN_PROGRESS: 'Z',
}

export interface IOrder {
    orderId: number
    status: string
    statusCode: string
    createDate: Date
    total: number
    storeId: number
    subtotal: number
    adminNote: string
    customerNote: string
    pickingHubId: number
    wasMovedToInProgress: boolean
    isFinalPurchaseOrder: boolean
    purchaseOrderId: string | null
    products: OrderProduct[]
}

export interface IOrderFactory extends Omit<IOrder, 'products'> {
    products: IOrderProductFactory[]
}

export class Order {
    orderId: number
    status: string
    statusCode: string
    createDate: Date
    total: number
    storeId: number
    subtotal: number
    adminNote: string
    customerNote: string
    pickingHubId: number
    wasMovedToInProgress: boolean
    isFinalPurchaseOrder: boolean
    purchaseOrderId: string | null
    products: OrderProduct[]
    _cycle: Cycle | null = null

    constructor(order: IOrder) {
        this.orderId = order.orderId
        this.status = order.status
        this.statusCode = order.statusCode
        this.createDate = order.createDate
        this.total = order.total
        this.storeId = order.storeId
        this.subtotal = order.subtotal
        this.adminNote = order.adminNote
        this.customerNote = order.customerNote
        this.pickingHubId = order.pickingHubId
        this.wasMovedToInProgress = order.wasMovedToInProgress
        this.isFinalPurchaseOrder = order.isFinalPurchaseOrder
        this.purchaseOrderId = order.purchaseOrderId
        this.products = order.products
    }

    isIncoming(): boolean {
        return this.cycle?.column?.step === Step.INCOMING
    }

    isInProgress(): boolean {
        return this.cycle?.column?.step === Step.IN_PROGRESS
    }

    canMoveOrderToReady(): boolean {
        return (
            this.cycle?.column?.step === Step.IN_PROGRESS &&
            this.products.every((product) => product.isLabeled)
        )
    }

    hasFinalPo(): boolean {
        return this.isFinalPurchaseOrder && this.purchaseOrderId !== null
    }

    get isCanceled(): boolean {
        return this.statusCode === OrderStatus.CANCELED
    }

    canRemoveFromPallet(): boolean {
        return this.cycle?.column?.step === Step.IN_PROGRESS && this.isCanceled
    }

    shouldGotIt(): boolean {
        return this.isCanceled
    }

    canMoveToInProgress(): boolean {
        return (
            this.cycle?.column?.step === Step.READY &&
            !this.wasMovedToInProgress
        )
    }

    canConsolidate(): boolean {
        return (
            this.cycle?.column?.step === Step.IN_PROGRESS &&
            this.products.filter(
                ({ isLabeled, isCanceled }) => !isLabeled && !isCanceled
            ).length > 1
        )
    }

    setCycle(cycle: Cycle) {
        this._cycle = cycle
    }

    get cycle(): Cycle {
        if (!this._cycle) {
            throw new Error('Orphan order was found: cycle wasn`t set')
        }

        return this._cycle
    }
}

export const createOrder = (orderData: IOrderFactory): Order => {
    const products = orderData.products.map(createOrderProduct)
    const order = new Order({
        ...orderData,
        products,
    })

    order.products.forEach((product) => product.setOrder(order))

    return order
}
